import React from 'react';
import classNames from 'classnames';
import { usePostLayoutSize } from './PostLayoutSizeContext';
import { isFeatureEnabled } from '../utils/features';
import styles from './PostLayoutContainer.module.scss';

const getPostLayoutClassName = (
  oldClassName: string | undefined,
  newClassName: string,
) => (isFeatureEnabled('new_post_layout') ? newClassName : oldClassName);

interface PostLayoutContainerProps {
  children: React.ReactNode;
  hasBanner?: boolean;
  hasStateBar?: boolean;
}

const PostLayoutContainer = React.forwardRef<
  HTMLElement,
  PostLayoutContainerProps
>(({ children, hasBanner, hasStateBar }, ref) => {
  const postLayoutSize = usePostLayoutSize();
  return (
    <article
      ref={ref}
      className={getPostLayoutClassName(
        'post-layout-container',
        classNames(styles.postLayoutContainer, styles[postLayoutSize], {
          [styles.hasBanner]: hasBanner,
          [styles.hasStateBar]: hasStateBar,
        }),
      )}
    >
      {children}
    </article>
  );
});

interface PostLayoutAboveProps {
  children?: React.ReactNode;
}

export const PostLayoutAbove = React.forwardRef<
  HTMLElement,
  PostLayoutAboveProps
>(({ children }, ref) => (
  <header
    ref={ref}
    className={getPostLayoutClassName(
      'post-layout-above',
      styles.postLayoutAbove,
    )}
  >
    {children}
  </header>
));

interface PostLayoutEditorProps {
  children: React.ReactNode;
}

export const PostLayoutEditor = ({ children }: PostLayoutEditorProps) => (
  <article
    className={getPostLayoutClassName(
      'post-layout-editor',
      styles.postLayoutEditor,
    )}
    aria-label="Post content"
  >
    {children}
  </article>
);

interface PostLayoutMagneticWallProps {
  children: React.ReactNode;
}

export const PostLayoutMagneticWall = ({
  children,
}: PostLayoutMagneticWallProps) => (
  <aside
    className={getPostLayoutClassName(
      'post-layout-comments',
      styles.postLayoutMagneticWall,
    )}
  >
    {children}
  </aside>
);

interface PostLayoutBelowProps {
  children: React.ReactNode;
}

export const PostLayoutBelow = ({ children }: PostLayoutBelowProps) => (
  <footer
    className={getPostLayoutClassName(
      'post-layout-below',
      styles.postLayoutBelow,
    )}
  >
    {children}
  </footer>
);

interface PostLayoutBannerProps {
  children: React.ReactNode;
  className: string;
}

export const PostLayoutBanner = React.forwardRef<
  HTMLDivElement,
  PostLayoutBannerProps
>(({ children, className }, ref) => (
  <div
    ref={ref}
    className={classNames(
      getPostLayoutClassName('post-layout-banner', styles.postLayoutBanner),
      className,
    )}
  >
    {children}
  </div>
));

interface PostLayoutControlsProps {
  children: React.ReactNode;
  className: string;
}

export const PostLayoutControls = ({
  children,
  className,
}: PostLayoutControlsProps) => (
  <div
    className={classNames(
      getPostLayoutClassName(
        'post-layout-banner-controls',
        styles.postLayoutBannerControls,
      ),
      className,
    )}
  >
    {children}
  </div>
);

interface PostLayoutTOCProps {
  children: React.ReactNode;
  className?: string;
}

export const PostLayoutTOC = ({ children, className }: PostLayoutTOCProps) => (
  <div className={classNames(className, styles.postLayoutTOC)}>{children}</div>
);

export const PostLayoutActions = ({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={classNames(className, styles.postLayoutActions)} {...rest}>
    {children}
  </div>
);

export const PostLayoutGutter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...rest }, ref) => (
  <div
    ref={ref}
    className={getPostLayoutClassName(
      className,
      classNames(className, styles.postLayoutGutter),
    )}
    {...rest}
  >
    {children}
  </div>
));

export default PostLayoutContainer;
